import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Button, TextField, Typography } from '@mui/material';
import logo from '../assets/swise_black.png';
import LeftSide from '../components/LeftSide'
import LanguageSelector from '../components/LanguageSelector';
import { useTranslation } from 'react-i18next';

import { validateUser } from '../api/userAPI'
import { generateOTP } from '../api/otpAPI'

const Login = () => {
  const navigate = useNavigate();
  const loginCardRef = useRef<HTMLDivElement>(null);
  const [loginHeight, setLoginHeight] = useState<number | undefined>(undefined);
  const [serverError, setServerError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  
  const [language, setLanguage] = useState<string>(() => {
    const stored = localStorage.getItem('language');
    return stored ? stored.toUpperCase() : 'EN';
  });

  const [errors, setErrors] = useState({
    email: false,
    emailFormat: false,
  });

  useEffect(() => {
    const storedLang = localStorage.getItem('language') || 'EN';
    i18n.changeLanguage(storedLang);
  }, []);


  useEffect(() => {
    const token = sessionStorage.getItem('authToken');
    if (token) {
      navigate('/viewer', { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!loginCardRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setLoginHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(loginCardRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    i18n.changeLanguage(lang.toLowerCase());
    localStorage.setItem('language', lang.toLowerCase());
  };


  const handleSubmit = async () => {
    setServerError(null);

    const normalizedEmail = email.trim().toLowerCase();

    const hasErrors = {
      email: normalizedEmail === '',
      emailFormat: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail),
    };

    setErrors(hasErrors);
    if (hasErrors.email || hasErrors.emailFormat) return;

    try {
      // Validate user
      const user = await validateUser(normalizedEmail);

      // Get user language
      localStorage.setItem('language', user.userLanguage);

      // Generate OTP
      await generateOTP(user.userName, user.userEmail, 'vdr');

      // Navigate to OTP verification
      navigate('/otp', {
        state: { email: normalizedEmail }
      });

    } catch (err: any) {
      console.error('[handleSubmit] Error:', err.message);
      setServerError(err.message || 'Unknown error');
    }
  };


  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Left Side */}
      <Box
        sx={{
          width: '60%',
          bgcolor: 'black',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <LeftSide minHeight={loginHeight} />
      </Box>

      {/* Right Side */}
      <Box
        sx={{
          width: '40%',
          bgcolor: 'black',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          ref={loginCardRef}
          sx={{
            width: '80%',
            bgcolor: 'white',
            borderRadius: 2,
            p: 4,
            boxShadow: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          <LanguageSelector selected={language} onChange={handleLanguageChange} />
          <img
            src={logo}
            alt="Swise Logo"
            style={{
              width: '100px',
              height: 'auto',
              marginBottom: '56px',
              alignSelf: 'flex-start',
            }}
          />

          <Typography
            gutterBottom
            sx={{
              alignSelf: 'flex-start',
              fontWeight: 'bold',
              fontSize: '25px',
              color: 'black',
            }}
          >
            {t('welcome')}
          </Typography>
          <Typography
            gutterBottom
            sx={{
              alignSelf: 'flex-start',
              fontWeight: 'normal',
              fontSize: '22px',
              color: 'grey',
            }}
          >
            {t('enterDetails')}
          </Typography>

          <TextField
            label={t('email')}
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email || errors.emailFormat}
            helperText={
              errors.email
                ? t('emailRequired')
                : errors.emailFormat
                ? t('emailInvalid')
                : ''
            }
          />

          {serverError && (
            <Typography sx={{ color: 'red', mt: 1, alignSelf: 'flex-start' }}>
              {serverError}
            </Typography>
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            sx={{
              mt: 2,
              height: '45px', // 56px == standard
              backgroundColor: '#73AF73',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#A8CFA9',
              },
            }}
          >
            {t('signIn')}
          </Button>

          {/* Section under the button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              mt: 3,
            }}
          >
            <Typography sx={{ fontSize: '16px', color: 'grey' }}>
              {t('needAccess')}{' '}
              <Typography
                component={Link}
                to="/signup"
                sx={{
                  color: '#A8CFA9',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#1976d2',
                  },
                }}
              >
                {t('signUp')}
              </Typography>
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  color: 'grey',
                }}
              >
                {t('havingProblems')}
              </Typography>
              <Typography
                component="a"
                href="mailto:it@swise.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: '16px',
                  color: '#A8CFA9',
                  mt: 0,
                  mb: 15,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#1976d2',
                  },
                }}
              >
                {t('contactSupport')}
              </Typography>
            </Box>
          </Box>

          {/* Copyright */}
          <Typography
            sx={{
              fontSize: '15px',
              color: 'grey',
              mt: 3,
              alignSelf: 'flex-start',
            }}
          >
            © 2025 Swise AG
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;